import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { User } from 'firebase/auth'
import type { Role } from '@/types/user'
import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext'
import { getRole, logout, subscribe } from './authService'

interface State {
  status: AuthStatus
  user: User | null
  role: Role | null
}

const INITIAL: State = { status: 'loading', user: null, role: null }

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(INITIAL)

  useEffect(() => {
    let active = true
    const unsubscribe = subscribe(async (user) => {
      if (!user) {
        if (active) setState({ status: 'unauthenticated', user: null, role: null })
        return
      }
      const role = await getRole(user)
      if (active) setState({ status: 'authenticated', user, role })
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({ ...state, logout }), [state])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
